/**
 * Formatting helpers shared across views.
 *
 * Time formatting uses the runtime Intl API to keep the bundle lean — the backend
 * already produces ISO 8601 strings, so the browser handles all locale work.
 */

export function formatTime(iso: string, tz?: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: tz
  });
}

export function formatDate(iso: string, tz?: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: tz
  });
}

export function formatDateTime(iso: string, tz?: string): string {
  return `${formatDate(iso, tz)} ${formatTime(iso, tz)}`;
}

export function durationMinutes(starts_at: string, ends_at: string): number {
  return Math.round((Date.parse(ends_at) - Date.parse(starts_at)) / 60000);
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`;
}

/**
 * Stable hash → HSL color for service colour-coding (Fresha pattern).
 * Deterministic so re-renders stay stable.
 */
export function colorForKey(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue} 60% 88%)`;
}

export function colorForKeyBorder(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue} 60% 45%)`;
}

export function startOfDay(iso: string, tz?: string): Date {
  const d = new Date(iso);
  if (!tz) {
    d.setHours(0, 0, 0, 0);
    return d;
  }
  const local = new Date(d.toLocaleString("en-US", { timeZone: tz }));
  local.setHours(0, 0, 0, 0);
  return local;
}

export function addDays(iso: string, days: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

export function startOfWeek(iso: string): Date {
  const d = new Date(iso);
  const day = d.getDay();
  const diff = (day + 6) % 7; // Monday-start
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function isoDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
